// src/pages/DocumentDetails.js
//
// Page dédiée aux détails d'un document (élève, professeur ou comptabilité).
// Route : /documents/:ownerType/:ownerId/:docId
//   - ownerType = 'students' | 'teachers' | 'accounting'
//   - ownerId   = uid de l'élève/prof, ou '_' pour un document comptable
//     (accounting_documents est une collection top-level, sans propriétaire)
//
// Accessible depuis Documents.js via le bouton "Voir" (icône œil) sur
// chaque ligne de document.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import {
  Box, Paper, Typography, Chip, Divider, Button, CircularProgress,
  IconButton, Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  TableChart as ExcelIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import moment from 'moment';
import 'moment/locale/fr';
moment.locale('fr');

const OWNER_LABELS = {
  students: 'élève',
  teachers: 'professeur',
  accounting: 'comptabilité',
};

function fileIconFor(fileType) {
  const t = (fileType || '').toLowerCase();
  if (t.includes('pdf')) return { icon: <PdfIcon fontSize="large" />, color: '#d32f2f' };
  if (t.includes('image')) return { icon: <ImageIcon fontSize="large" />, color: '#1976d2' };
  if (t.includes('sheet') || t.includes('excel')) return { icon: <ExcelIcon fontSize="large" />, color: '#2e7d32' };
  return { icon: <FileIcon fontSize="large" />, color: '#757575' };
}

export default function DocumentDetails() {
  const { ownerType, ownerId, docId } = useParams();
  const navigate = useNavigate();

  const [docData, setDocData] = useState(null);
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isAccounting = ownerType === 'accounting';
  const docPath = isAccounting
    ? ['accounting_documents', docId]
    : [`${ownerType}/${ownerId}/documents`, docId];

  const loadData = async (showFullLoader = true) => {
    if (showFullLoader) setLoading(true);
    else setRefreshing(true);
    try {
      const snap = await getDoc(doc(db, ...docPath));
      if (!snap.exists()) {
        setNotFound(true);
        return;
      }
      const data = snap.data();
      setDocData({
        id: snap.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
        uploadedAt: data.uploadedAt?.toDate ? data.uploadedAt.toDate() : data.uploadedAt,
      });

      if (!isAccounting && ownerId && ownerId !== '_') {
        const ownerSnap = await getDoc(doc(db, ownerType, ownerId));
        if (ownerSnap.exists()) setOwner({ id: ownerSnap.id, ...ownerSnap.data() });
      }
    } catch (err) {
      console.error('Erreur chargement document :', err);
      setNotFound(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerType, ownerId, docId]);

  const handleDelete = async () => {
    if (!docData) return;
    if (!window.confirm('Supprimer ce document définitivement ?')) return;
    setDeleting(true);
    try {
      if (docData.storagePath) {
        try {
          await deleteObject(ref(storage, docData.storagePath));
        } catch (e) {
          // Le fichier Storage a pu déjà être supprimé — on continue quand
          // même pour nettoyer la fiche Firestore plutôt que de bloquer.
          console.warn('Suppression Storage :', e);
        }
      }
      await deleteDoc(doc(db, ...docPath));
      navigate('/documents');
    } catch (err) {
      console.error('Erreur suppression document :', err);
      window.alert("Erreur lors de la suppression du document.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (notFound || !docData) {
    return (
      <Box p={3}>
        <Typography>Document introuvable.</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/documents')}>Retour</Button>
      </Box>
    );
  }

  const { icon, color } = fileIconFor(docData.fileType);
  const isUserUpload = docData.uploadedBy && docData.uploadedBy !== 'admin';
  const displayTitle = isUserUpload && docData.title ? docData.title : docData.fileName;

  return (
    <Box p={3} sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      {/* ── En-tête ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/documents')}>
          Retour
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Actualiser">
          <IconButton onClick={() => loadData(false)} disabled={refreshing}>
            {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Infos document ───────────────────────────────────────────────── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 64, height: 64, borderRadius: '14px',
              backgroundColor: `${color}1A`, color, flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {displayTitle || 'Document sans nom'}
            </Typography>
            {isUserUpload && docData.title && docData.fileName && (
              <Typography variant="body2" color="text.secondary">{docData.fileName}</Typography>
            )}
          </Box>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          {docData.categoryName && <Chip label={docData.categoryName} variant="outlined" />}
          <Chip
            label={ownerType === 'accounting' ? 'Comptabilité' : OWNER_LABELS[ownerType]}
            variant="outlined"
          />
          <Chip
            label={isUserUpload ? '📤 Envoyé par l\'utilisateur' : 'Ajouté par l\'admin'}
            color={isUserUpload ? 'secondary' : 'default'}
          />
          {!isAccounting && (
            <Chip
              label={docData.visibleByUser ? 'Visible par l\'utilisateur' : 'Interne uniquement'}
              color={docData.visibleByUser ? 'success' : 'default'}
              variant={docData.visibleByUser ? 'filled' : 'outlined'}
            />
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {owner && (
          <Typography sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon fontSize="small" color="action" />
            <strong>{OWNER_LABELS[ownerType] === 'élève' ? 'Élève' : 'Professeur'} :</strong>{' '}
            {owner.firstName} {owner.lastName}
          </Typography>
        )}
        {docData.createdAt && (
          <Typography sx={{ mb: 1 }}>
            <strong>Ajouté le :</strong> {moment(docData.createdAt).format('dddd D MMMM YYYY à HH:mm')}
          </Typography>
        )}
        {docData.note && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {docData.note}
            </Typography>
          </>
        )}
      </Paper>

      {/* ── Actions ──────────────────────────────────────────────────────── */}
      <Paper sx={{ p: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          href={docData.downloadURL}
          target="_blank"
          download
          disabled={!docData.downloadURL}
          sx={{ bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' } }}
        >
          Télécharger
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDelete}
          disabled={deleting}
        >
          {deleting ? 'Suppression...' : 'Supprimer'}
        </Button>
      </Paper>
    </Box>
  );
}
